import { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const VoiceRecorder = ({ onRecorded, label = "Spreek in", hint, compact = false, pill = false, testId = "voice-record" }) => {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => () => { clearInterval(timerRef.current); recorderRef.current?.stream?.getTracks().forEach((t) => t.stop()); }, []);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(timerRef.current);
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        if (blob.size < 1000) { toast.error("Opname te kort — probeer opnieuw"); return; }
        setBusy(true);
        try { await onRecorded(blob); } finally { setBusy(false); setSeconds(0); }
      };
      recorderRef.current = rec;
      rec.start();
      setSeconds(0);
      setRecording(true);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch {
      toast.error("Microfoon niet beschikbaar — sta toegang toe in je browser");
    }
  };

  const stop = () => recorderRef.current?.state === "recording" && recorderRef.current.stop();

  if (pill) {
    if (busy) {
      return (
        <span data-testid={`${testId}-processing`} className="flex items-center gap-1.5 rounded-full bg-secondary text-xs font-semibold px-3.5 py-2 text-muted-foreground">
          <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" /> Verwerken...
        </span>
      );
    }
    return !recording ? (
      <button data-testid={`${testId}-button`} type="button" onClick={start}
        className="flex items-center gap-1.5 rounded-full bg-secondary hover:bg-secondary/70 text-xs font-semibold px-3.5 py-2 transition-[background-color] duration-200">
        <Mic className="w-3.5 h-3.5 text-[#B5838D]" /> {label}
      </button>
    ) : (
      <button data-testid={`${testId}-stop-button`} type="button" onClick={stop}
        className="flex items-center gap-1.5 rounded-full bg-red-500 hover:bg-red-600 text-white text-xs font-bold px-3.5 py-2 animate-pulse transition-[background-color] duration-200">
        <Square className="w-3 h-3 fill-current" /> Stop ({Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")})
      </button>
    );
  }

  if (compact) {
    if (busy) {
      return (
        <span data-testid="voice-processing" className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center shrink-0">
          <Loader2 className="w-4 h-4 animate-spin text-primary" />
        </span>
      );
    }
    return !recording ? (
      <button data-testid="voice-record-button" type="button" onClick={start} title="Spreek een moment in"
        className="w-9 h-9 rounded-full bg-[#B5838D] hover:bg-[#8E5D67] text-white flex items-center justify-center shrink-0 active:scale-95 transition-[transform,background-color] duration-200">
        <Mic className="w-4 h-4" />
      </button>
    ) : (
      <button data-testid="voice-stop-button" type="button" onClick={stop} title="Stop opname"
        className="w-9 h-9 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shrink-0 animate-pulse active:scale-95 transition-[transform,background-color] duration-200">
        <Square className="w-3.5 h-3.5 fill-current" />
      </button>
    );
  }

  if (busy) {
    return (
      <div data-testid="voice-processing" className="flex items-center gap-2 text-sm text-muted-foreground py-2">
        <Loader2 className="w-4 h-4 animate-spin text-primary" /> AI verwerkt je opname...
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3">
      {!recording ? (
        <button data-testid="voice-record-button" type="button" onClick={start}
          className="flex items-center gap-2 rounded-full bg-[#B5838D] hover:bg-[#8E5D67] text-white text-sm font-semibold px-4 py-2.5 active:scale-95 transition-[transform,background-color] duration-200">
          <Mic className="w-4 h-4" /> {label}
        </button>
      ) : (
        <button data-testid="voice-stop-button" type="button" onClick={stop}
          className="flex items-center gap-2 rounded-full bg-red-500 hover:bg-red-600 text-white text-sm font-semibold px-4 py-2.5 animate-pulse active:scale-95 transition-[transform,background-color] duration-200">
          <Square className="w-4 h-4 fill-current" /> Stop ({Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")})
        </button>
      )}
      {hint && !recording && <p className="text-xs text-muted-foreground">{hint}</p>}
      {recording && <p className="text-xs text-red-500 font-semibold">Opname loopt...</p>}
    </div>
  );
};
